/**
 * Fias Forge arche — client-side types (ADR-023).
 *
 * Wire shapes come from `@/lib/api` (mirrors of the server's serializers);
 * everything here is page state that never crosses the network.
 */
export type {
  ForgeSummary,
  SharedForgeSummary,
  ForgeStatus,
  ForgeModelTier,
  ForgeSize,
  ForgeTurnSummary,
  ForgeTestBuildSummary,
  ForgeTestChannelInfo,
  ForgeUsage,
} from '@/lib/api';

/** A chat bubble in the forge conversation pane. */
export interface ForgeMessage {
  role: 'user' | 'assistant';
  content: string;
  /** ISO timestamp; also the key that binds tool events to the bubble. */
  timestamp: string;
}

/** A screenshot the runtime pushed during a turn (`forge_screenshot`). */
export interface ForgeScreenshot {
  /** Workspace-relative path the runtime wrote the PNG to. */
  path: string;
  /** `data:image/png;base64,…` — ready for an `<img src>`. */
  dataUrl: string;
  requestId: string;
  receivedAt: string;
}

/** A tool call / result inside a turn, shaped for the shared VerboseTimeline. */
export interface ForgeToolEvent {
  type: 'tool_call' | 'tool_result';
  timestamp: string;
  data: {
    toolName?: string;
    toolInput?: unknown;
    toolResult?: unknown;
    isError?: boolean;
  };
}

/** Forge statuses in which the runtime is (or is becoming) alive. */
export const FORGE_ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'provisioning',
  'running',
  'stopping',
]);

/** Statuses the page polls through: the platform will move them without user input. */
export const FORGE_TRANSITIONAL_STATUSES: ReadonlySet<string> = new Set([
  'provisioning',
  'stopping',
]);
